import { Box, BoxProps, Heading, Text, Code, Table, Link, List } from '@chakra-ui/react';
import Markdown, { Components } from 'react-markdown';

interface MarkdownViewerProps {
  content: string;
  rootProps?: BoxProps;
}

const markdownComponents: Components = {
  h1: ({ children }) => (
    <Heading as='h1' size='xl' mb='4' mt='6' borderBottomWidth='1px' pb='2'>
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as='h2' size='lg' mb='3' mt='5' borderBottomWidth='1px' pb='2'>
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as='h3' size='md' mb='2' mt='4'>
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading as='h4' size='sm' mb='2' mt='3'>
      {children}
    </Heading>
  ),
  p: ({ children }) => (
    <Text mb='3' lineHeight='tall'>
      {children}
    </Text>
  ),
  a: ({ href, children }) => (
    <Link href={href} color='blue.500' textDecoration='underline'>
      {children}
    </Link>
  ),
  ul: ({ children }) => (
    <List.Root as='ul' mb='3' ml='4'>
      {children}
    </List.Root>
  ),
  ol: ({ children }) => (
    <List.Root as='ol' mb='3' ml='4'>
      {children}
    </List.Root>
  ),
  li: ({ children }) => <List.Item mb='1'>{children}</List.Item>,
  code: ({ children, className }) => {
    const isInline = !className;
    if (isInline) {
      return (
        <Code px='1' py='0.5' borderRadius='sm' fontSize='sm'>
          {children}
        </Code>
      );
    }
    return (
      <Box
        as='pre'
        bg='gray.100'
        _dark={{ bg: 'gray.800' }}
        p='4'
        borderRadius='md'
        overflowX='auto'
        mb='3'
        fontSize='sm'>
        <code>{children}</code>
      </Box>
    );
  },
  pre: ({ children }) => <>{children}</>,
  table: ({ children }) => (
    <Box overflowX='auto' mb='4'>
      <Table.Root size='sm' variant='outline'>
        {children}
      </Table.Root>
    </Box>
  ),
  thead: ({ children }) => <Table.Header>{children}</Table.Header>,
  tbody: ({ children }) => <Table.Body>{children}</Table.Body>,
  tr: ({ children }) => <Table.Row>{children}</Table.Row>,
  th: ({ children }) => (
    <Table.ColumnHeader fontWeight='bold' bg='gray.50' _dark={{ bg: 'gray.700' }}>
      {children}
    </Table.ColumnHeader>
  ),
  td: ({ children }) => <Table.Cell>{children}</Table.Cell>,
  blockquote: ({ children }) => (
    <Box
      as='blockquote'
      borderLeftWidth='4px'
      borderLeftColor='blue.500'
      pl='4'
      py='2'
      mb='3'
      bg='gray.50'
      _dark={{ bg: 'gray.800' }}>
      {children}
    </Box>
  ),
  hr: () => <Box as='hr' my='6' borderColor='gray.200' _dark={{ borderColor: 'gray.600' }} />
};

export const MarkdownViewer = ({ content, rootProps }: MarkdownViewerProps) => {
  return (
    <Box {...rootProps}>
      <Markdown components={markdownComponents}>{content}</Markdown>
    </Box>
  );
};
